import { format } from 'date-fns';
import type { Program } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, type DatePickerDateType, Form, Loading, Stepper, getDateType, getTimeType } from 'ui';
import { type Activity, type PeriodicityType, type Reminder, type Task, isBloodPressureType } from 'models/activity';
import { ActivityFormStep3 } from './features/activity-form-step3';
import type { ActivityOption, TaskKindOption } from './features/activity-types';
import ActivityFormStep1 from './features/ActivityFormStep1';
import ActivityFormStep2 from './features/ActivityFormStep2';
import ActivityFormStep4 from './features/ActivityFormStep4';
import { useGetActivityInitialValues } from './hooks';

export type ActivityFormTime = {
  id?: string;
  time: DatePickerDateType;
};

export type ActivityFormValues = {
  description: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  end_time: DatePickerDateType;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  html_description: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  is_withings: boolean;
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  periodicity_type?: PeriodicityType;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  programs_topic_id: string;
  reminders: Reminder[];
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  start_time: DatePickerDateType;
  tasks: Array<{ bulk?: string[] } & Task>;
  times: ActivityFormTime[];
};

type P = {
  activity: Activity;
  edit?: boolean;
  loading: boolean;
  onRequestReturn?: () => void;
  onSubmit: (activity: Activity) => void;
  program: Program;
};

const defaultTextTask = (name?: string): Task => ({ input: 'text', kind: 'default', name: name ?? '' });
const defaultNumberTask = (name?: string, kind?: TaskKindOption): Task => ({
  input: 'number',
  kind: kind ?? 'default',
  name: name ?? '',
});
const defaultSelectTask = (name?: string): { bulk: string[] } & Task => ({
  bulk: [''],
  input: 'select',
  kind: 'default',
  name: name ?? '',
});

const ActivityForm = ({ activity, edit, loading, onRequestReturn, onSubmit, program }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ActivityFormValues>();
  const initialValues = useGetActivityInitialValues({ activity, edit: edit ?? false });
  const [isWithings, setIsWithings] = useState(Boolean(initialValues.is_withings));

  const isDisabledEditWithings = Boolean(initialValues.is_withings && edit);
  const isDisabledEdit = Boolean(program.status !== 'draft' && edit);

  const [step, setStep] = useState(0);

  const isSelectFeedback = activity.tasks.length > 1;
  const isNumberFeedback = activity.tasks.find((task) => task.input === 'number');

  const [activityType, setActivityType] = useState<ActivityOption>(() => {
    if (isBloodPressureType(activity)) {
      return 'withings-blood-pressure';
    }
    if (isSelectFeedback) {
      return 'select-feedback';
    }
    if (isNumberFeedback) {
      return 'numeric-feedback';
    }
    return 'extended-feedback';
  });

  const onSelectActivityType = (type: ActivityOption) => {
    setActivityType(type);
    onSelectWithings(false);

    let tasks: Array<{ bulk?: string[] } & Task>;
    switch (type) {
      case 'extended-feedback':
        tasks = [defaultTextTask()];
        break;
      case 'numeric-feedback':
        tasks = [defaultNumberTask()];
        break;
      case 'select-feedback':
        tasks = [defaultSelectTask(), defaultSelectTask(t('Unit')), defaultNumberTask(t('Numeric value'))];
        break;

      case 'withings-blood-pressure':
        tasks = [
          defaultNumberTask('Diastolic Blood Pressure (in mmHg)', 'withings_blood_pressure_diastolic'),
          defaultNumberTask('Systolic Blood Pressure (in mmHg)', 'withings_blood_pressure_systolic'),
        ];
        break;
    }

    form.setFieldsValue({
      periodicity_type: undefined,
      tasks,
    });
  };

  const onSelectTaskKind = (kind: TaskKindOption) => {
    onSelectWithings(kind !== 'default');
  };

  const onSelectWithings = (isSelectedWithings: boolean) => {
    setIsWithings(isSelectedWithings);

    form.setFieldsValue({ is_withings: isSelectedWithings });
  };

  const getStep = (currentStep: number) => {
    switch (currentStep + 1) {
      case 1:
        return (
          <ActivityFormStep1
            endTime={program.endTime}
            form={form}
            startTime={program.startTime}
            topics={program.topics}
          />
        );
      case 2:
        return (
          <ActivityFormStep2
            disabledEdit={isDisabledEdit || isDisabledEditWithings}
            isWithingsEnabled={(program.withingsDevices?.length ?? 0) > 0}
            onSelect={onSelectActivityType}
            selected={activityType}
          />
        );
      case 3:
        return (
          <ActivityFormStep3
            disabledEdit={isDisabledEdit}
            disabledEditWithings={isDisabledEditWithings}
            onSelectTaskKind={onSelectTaskKind}
            type={activityType}
          />
        );
      default:
        return <ActivityFormStep4 disabledEdit={isDisabledEdit} programId={program.id} />;
    }
  };

  const onNavigateTo = (nextStep: number) => {
    if (activityType === 'withings-blood-pressure' && nextStep === 2) {
      setStep(nextStep - 1);
      return;
    }

    setStep(nextStep);
  };

  const onSubmitStep = () => {
    form
      .validateFields()
      .then(() => {
        if (step === 1 && activityType === 'withings-blood-pressure') {
          onSelectWithings(true);
          setStep(step + 2);
        } else if (step < 3) {
          setStep(step + 1);
        } else {
          onFinish(form.getFieldsValue());
        }

        return null;
      })

      // eslint-disable-next-line @typescript-eslint/use-unknown-in-catch-callback-variable -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
        if (error?.errorFields?.[0]?.name) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/no-unsafe-member-access -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          form?.scrollToField(error.errorFields[0].name, {
            behavior: 'smooth',
            block: 'center',
          });
        }
      });
  };

  const onFinish = (data: ActivityFormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: fix
    const cleanedTasks = data.tasks.map((task, index) => {
      if (task.input === 'select') {
        const bulkOptions = (task.bulk?.[0] ?? '')
          .split(',')
          .filter(Boolean)
          .map((option, indexOption) => ({
            ...activity.tasks[index]?.options?.[indexOption],
            name: option,
          }));

        return {
          ...activity.tasks[index],
          input: task.input,
          kind: task.kind,
          name: task.name,
          options: bulkOptions,
        };
      }

      return {
        ...activity.tasks[index],
        ...task,
      };
    }) as Task[];

    const isLockedForEdit = program.status === 'published' && edit;

    const cleanedActivity: Activity = {
      ...activity,
      description: data.description,
      endTime: getDateType(data.end_time),
      htmlDescription: data.html_description,
      isWithings,
      multipleSubmissions: activityType === 'select-feedback' || isWithings,
      name: data.name,
      periodicityType: data.periodicity_type,
      programsTopicId: data.programs_topic_id,
      reminders: isLockedForEdit
        ? activity.reminders
        : data.reminders.map((reminder: Reminder, index: number) => ({
            ...activity.reminders?.[index],
            minutes: reminder.minutes * (reminder.type ?? 1),
          })),
      startTime: getDateType(data.start_time),
      tasks: isLockedForEdit ? activity.tasks : cleanedTasks,
      times: isLockedForEdit
        ? activity.times
        : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: need to check
          (data.times ?? []).map((time, index) => ({
            ...activity.times[index],
            time: format(getTimeType(time.time), 'HH:mm:ssX'),
          })),
    };

    onSubmit(cleanedActivity);
  };

  return loading ? (
    <Col style={{ fontSize: 24, textAlign: 'center' }}>
      <Loading />
    </Col>
  ) : (
    <Form<ActivityFormValues> form={form} initialValues={initialValues}>
      <Stepper
        currentStep={step}
        getStep={getStep}
        numberOfSteps={4}
        onNavigateTo={onNavigateTo}
        onRequestReturn={onRequestReturn}
        onSubmit={onSubmitStep}
      />
    </Form>
  );
};

export default ActivityForm;
